import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionId, getToken } from '@/utils/storage';
import { getStoredGuestExamUser } from '@/api/exam';
import { ProgressBar } from '@/components/ProgressBar';
import { Check } from 'lucide-react';

const CombinedFeedbackProcessing = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isComplete, setIsComplete] = useState(false); // NEW
  const [feedbackData, setFeedbackData] = useState<any | null>(null);
  const navigate = useNavigate();

  // UX: keep messages simple, reassuring, non-technical
  const statusMessages = [
    'Reviewing your speaking exam...',
    'Measuring your level and key skills...',
    'Preparing your personalized feedback...',
  ];

  useEffect(() => {
    // Ensure microphone/audio context is stopped when arriving on processing screen
    (async () => {
      try {
        const globalAudioRef = (window as any).audioServiceRef?.current;
        if (globalAudioRef) {
          try {
            console.log('🔊 CombinedFeedbackProcessing: stopping global audio service');
            globalAudioRef.stopRecording?.();
            globalAudioRef.cancelAIAudio?.();
            globalAudioRef.cleanup?.();
          } catch (e) {
            console.warn('Error stopping global audio service:', e);
          }
        }
      } catch (e) {
        // ignore
      }

      try {
        if (navigator && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          try {
            s.getTracks().forEach((t) => {
              try {
                (MediaStreamTrack.prototype as any).stop.call(t);
              } catch {
                try {
                  t.stop();
                } catch {}
              }
            });
          } catch {}
        }
      } catch (e) {
        console.warn('Could not stop media tracks on processing screen:', e);
      }
      try {
        const ctx: AudioContext | undefined = (window as any).__appAudioContext;
        if (ctx && typeof ctx.close === 'function') {
          ctx.close().catch(() => {});
          (window as any).__appAudioContext = undefined;
        }
      } catch (e) {
        console.warn('Error closing global audio context:', e);
      }
    })();

    // Cycle through status messages
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);

    // Process combined feedback in background
    const processCombinedFeedback = async () => {
      try {
        const sessionId = getSessionId();

        // 1) Get tokens
        const paidToken = getToken();
        const guestUser = getStoredGuestExamUser();
        const guestToken = guestUser?.token ?? localStorage.getItem('guestToken');

        // 2) Decide which mode
        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;

        console.log('🔐 COMBINED FEEDBACK - Authentication details:', {
          hasPaidToken: !!paidToken,
          hasGuestUser: !!guestUser,
          hasGuestToken: !!guestToken,
          isGuest,
          tokenType: paidToken ? 'paid' : 'guest',
          sessionId,
          guestUserId: guestUser?.user_id,
          guestUserData: guestUser,
        });

        if (!sessionId || !token) {
          console.error('❌ COMBINED FEEDBACK - Session or authentication not found:', {
            sessionId,
            token,
            paidToken,
            guestToken,
          });
          setIsProcessing(false);
          return;
        }

        if (isGuest) {
          console.log(
            '🎯 GUEST COMBINED FEEDBACK - Bypassing fluency metrics, calling guest-specific combined analysis...',
          );
          try {
            const feedbackPayload = {
              session_id: sessionId,
              guest_id: guestUser?.user_id,
              is_guest: true,
            };

            console.log(
              '🔐 GUEST COMBINED FEEDBACK - Sending request to guest-combined-feedfack-demo:',
              {
                payload: feedbackPayload,
              },
            );

            const feedbackResponse = await fetch(
              'https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/guest-full-feedback',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(feedbackPayload),
              },
            );

            if (feedbackResponse.ok) {
              const feedbackResult = await feedbackResponse.json();
              console.log('✅ GUEST COMBINED FEEDBACK - Comprehensive feedback generated:', feedbackResult);

              if (feedbackResult.success && feedbackResult.feedback) {
                setFeedbackData(feedbackResult.feedback);
                setIsProcessing(false);

                localStorage.setItem(
                  'combined_feedback_data',
                  JSON.stringify(feedbackResult.feedback),
                );

                // Let the progress bar complete visually, then navigate
                setIsComplete(true);
                return;
              } else {
                throw new Error('Invalid feedback response structure from guest endpoint');
              }
            } else {
              const errorText = await feedbackResponse.text();
              console.error(
                '❌ GUEST COMBINED FEEDBACK - Feedback analysis failed:',
                feedbackResponse.status,
                errorText,
              );
              throw new Error(`Guest feedback analysis failed: ${feedbackResponse.status}`);
            }
          } catch (error) {
            console.error('❌ GUEST COMBINED FEEDBACK - Error generating feedback:', error);
            console.log('⚠️ GUEST COMBINED FEEDBACK - Falling back to results page');
            setIsProcessing(false);
            navigate('/comprehensive-feedback-results');
          }
          return; // IMPORTANT: Stop execution for guest users here
        }

        console.log(
          '🎯 COMBINED FEEDBACK PROCESSING - Starting comprehensive analysis for PAID user...',
        );
        console.log(
          '🎯 COMBINED FEEDBACK PROCESSING - Proceeding to combined feedback analysis...',
        );

        try {
          // Get topic IDs from localStorage
          const task2TopicId = localStorage.getItem('task2_topic_id');
          const task3TopicId = localStorage.getItem('task3_topic_id');

          // Build combined feedback payload
          const feedbackPayload: any = {
            session_id: sessionId,
            task2_topic_id: task2TopicId || null,
            task3_topic_id: task3TopicId || null,
          };

          if (isGuest) {
            feedbackPayload.guest_id = guestUser?.user_id;
            feedbackPayload.is_guest = true;
          }

          console.log('🔐 COMBINED FEEDBACK - Sending request:', {
            payload: feedbackPayload,
            token: token ? `${token.substring(0, 20)}...` : 'none',
            isGuest,
            guestUserId: guestUser?.user_id,
            guestUserData: guestUser,
            task2TopicId,
            task3TopicId,
          });

          const feedbackResponse = await fetch(
            'https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/combine-3',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(feedbackPayload),
            },
          );

          if (feedbackResponse.ok) {
            const feedbackResult = await feedbackResponse.json();
            console.log(
              '✅ COMBINED FEEDBACK PROCESSING - Comprehensive feedback generated:',
              feedbackResult,
            );

            if (feedbackResult.success && feedbackResult.feedback) {
              setFeedbackData(feedbackResult.feedback);
              setIsProcessing(false);

              // Store feedback data in localStorage for the results page
              localStorage.setItem(
                'combined_feedback_data',
                JSON.stringify(feedbackResult.feedback),
              );

              // Let the progress bar complete visually, then navigate
              setIsComplete(true);
              return;
            } else {
              throw new Error('Invalid feedback response structure');
            }
          } else {
            const errorText = await feedbackResponse.text();
            console.error(
              '❌ COMBINED FEEDBACK PROCESSING - Feedback analysis failed:',
              feedbackResponse.status,
              errorText,
            );
            throw new Error(`Feedback analysis failed: ${feedbackResponse.status}`);
          }
        } catch (error) {
          console.error('❌ COMBINED FEEDBACK PROCESSING - Error generating feedback:', error);

          // Fallback: redirect to regular results page
          console.log('⚠️ COMBINED FEEDBACK PROCESSING - Falling back to regular results page');
          setIsProcessing(false);
          navigate('/comprehensive-feedback-results');
        }
      } catch (error) {
        console.error('❌ COMBINED FEEDBACK PROCESSING - Error processing results:', error);
        setIsProcessing(false);
        // Redirect to results page anyway (it will handle the error)
        navigate('/comprehensive-feedback-results');
      }
    };

    processCombinedFeedback();

    return () => {
      clearInterval(messageInterval);
    };
  }, [navigate, statusMessages.length]);

  // Navigate only after the bar reaches 100%
  useEffect(() => {
    if (!isComplete) return;

    const timeout = setTimeout(() => {
      navigate('/comprehensive-feedback-results');
    }, 400);

    return () => clearTimeout(timeout);
  }, [isComplete, navigate]);

  return (
    <div
      className="
        flex flex-col items-center justify-center
        relative overflow-hidden
        h-full
        px-4 py-12
        md:px-6 md:py-16
      "
    >
      {/* Main Content */}
      <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
        {/* Processing Icon */}
        <div className="relative mb-6">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Preparing Your Results
        </h1>

        {/* Status Messages */}
        <div className="space-y-3 mb-6">
          <p className="text-lg md:text-xl text-muted-foreground">
            {statusMessages[currentMessageIndex]}
          </p>
        </div>

        {/* Progress Bar */}
        <ProgressBar isComplete={isComplete} />

        {/* Additional Info */}
        <div className="mt-8 text-sm text-muted-foreground max-w-xl mx-auto">
          <p>
            This usually takes less than a minute. Please keep this tab open so your results and
            next-step tips can load.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CombinedFeedbackProcessing;
